import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Nav, NavItem, NavLink } from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { Spinner } from "reactstrap";

const apiClient = new APIClient();

const formatAmount = (amount) => {
  if (amount == null || amount === "") return "0.00";
  const num = parseFloat(amount);
  return Number.isNaN(num)
    ? "0.00"
    : "₹" +
        num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
};

const getPartyName = (row) => row?.invoiceParty?.name ?? row?.invoice?.party?.name ?? "—";
const getInvoiceNo = (row) => row?.invoice?.invoiceNo ?? "—";
const getSubtype = (row) =>
  row?.invoice?.invoiceSubtype?.description ?? row?.invoice?.invoiceSubtype?.subtype ?? "—";

const TopExpenses = () => {
  const [activeTab, setActiveTab] = useState("amount");
  const [entries, setEntries] = useState([]);
  const [bySubtype, setBySubtype] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchByAmount = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/dashboard/top-expenses", { by: "amount", limit: 10 });
      const raw = response?.data ?? response;
      const payload = Array.isArray(raw) ? raw[0] : raw;
      const list = payload?.entries ?? (Array.isArray(payload) ? payload : []);
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || "Failed to load top expenses");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBySubtype = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/dashboard/top-expenses", { by: "subtype", limit: 10 });
      const raw = response?.data ?? response;
      const payload = Array.isArray(raw) ? raw[0] : raw;
      const list = payload?.bySubtype ?? (Array.isArray(payload) ? payload : []);
      setBySubtype(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || "Failed to load expense categories");
      setBySubtype([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "amount") fetchByAmount();
    else fetchBySubtype();
  }, [activeTab]);

  return (
    <Col xl={4}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Top Expenses</h4>
          <Nav pills className="flex-shrink-0">
            <NavItem>
              <NavLink
                className={activeTab === "amount" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("amount");
                }}
                href="#/"
              >
                By Amount
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "subtype" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("subtype");
                }}
                href="#/"
              >
                By Category
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-0">{error}</div>
          ) : activeTab === "amount" ? (
            <div className="table-responsive table-card">
              <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                <thead className="text-muted table-light">
                  <tr>
                    <th scope="col">Invoice No</th>
                    <th scope="col">Party</th>
                    <th scope="col">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(entries || []).map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span className="fw-medium text-body">{getInvoiceNo(row)}</span>
                      </td>
                      <td>{getPartyName(row)}</td>
                      <td>
                        <span className="text-danger fw-medium">{formatAmount(row.debitAmount)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-responsive table-card">
              <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                <thead className="text-muted table-light">
                  <tr>
                    <th scope="col">Category</th>
                    <th scope="col">Total</th>
                    <th scope="col">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {(bySubtype || []).map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className="fw-medium text-body">{row.subtype || "—"}</span>
                      </td>
                      <td>
                        <span className="text-danger fw-medium">{formatAmount(row.totalDebit)}</span>
                      </td>
                      <td>{row.count ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default TopExpenses;
